import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { format } from 'date-fns';
import { BehaviorSubject, combineLatest, map, take } from 'rxjs';
import { CalendarTableHelper } from 'server/shared/helpers/calendarTableHelper';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { CustomTableHeaderComponent } from '../../../../../shared/modules/custom-table/custom-table-header/custom-table-header.component';

@Component({
  selector: 'app-custom-availability-table-header',
  templateUrl: './custom-availability-table-header.component.html',
  styleUrls: [
    './custom-availability-table-header.component.scss',
    '../availability-table-shared-styles.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomAvailabilityTableHeaderComponent {
  calendarTableHelper$ = new BehaviorSubject<CalendarTableHelper | undefined>(
    undefined,
  );
  @Input() set calendarTableHelper(value: CalendarTableHelper | undefined) {
    this.calendarTableHelper$.next(value);
  }
  get calendarTableHelper() {
    return this.calendarTableHelper$.value;
  }

  activeDayIndex$ = new BehaviorSubject<number | undefined>(undefined);
  @Input() set activeDayIndex(value: number | undefined) {
    this.activeDayIndex$.next(value);
  }
  get activeDayIndex() {
    return this.activeDayIndex$.value;
  }

  activeDayRange$ = new BehaviorSubject<number | undefined>(undefined);
  @Input() set activeDayRange(value: number | undefined) {
    this.activeDayRange$.next(value);
  }
  get activeDayRange() {
    return this.activeDayRange$.value;
  }

  @Output() daySelected = new EventEmitter<string>();
  @ViewChild(CustomTableHeaderComponent)
  tableHeaderComponent?: CustomTableHeaderComponent;

  hoveredDayIndex$ = new BehaviorSubject<number | undefined>(undefined);

  today$ = new BehaviorSubject<string>(format(new Date(), 'yyyy.MM.dd'));

  selectionInfo$ = combineLatest([
    this.activeDayIndex$,
    this.activeDayRange$,
    this.hoveredDayIndex$,
  ]).pipe(
    map(([startIndex, range, hoveredIndex]) => {
      if (startIndex === undefined) {
        return {
          startIndex: undefined,
          endIndex: undefined,
          selectedIndexes: new Set<number>(),
          hoveredIndex,
          hasHover: hoveredIndex !== undefined,
          isHoverAfterStart: false,
          isHoverBeforeStart: false,
        };
      }
      const endIndex = startIndex + (range || 0);
      const selectedIndexes = new Set<number>();

      for (let i = startIndex; i <= endIndex; i++) {
        selectedIndexes.add(i);
      }
      return {
        startIndex,
        endIndex,
        selectedIndexes,
        hoveredIndex,
        hasHover: hoveredIndex !== undefined,
        isHoverAfterStart:
          hoveredIndex !== undefined && hoveredIndex > startIndex,
        isHoverBeforeStart:
          hoveredIndex !== undefined && hoveredIndex < startIndex,
      };
    }),
  );

  // unixDate format
  dragStart?: string;

  constructor() {}

  selectDayMouseDown(day: any, event: MouseEvent) {
    if (event.button !== 0) return;
    combineLatest([this.activeDayIndex$, this.activeDayRange$])
      .pipe(take(1))
      .subscribe(([activeDayIndex, activeDayRange]) => {
        const dayIsSelected =
          activeDayIndex !== undefined &&
          (day.totalIndex === activeDayIndex ||
            (day.totalIndex > activeDayIndex &&
              day.totalIndex <= activeDayIndex + (activeDayRange || 0)));

        if (dayIsSelected) {
          // clicking a selected day triggers a range clear in the parent component
          this.selectDay(day.date);
          delete this.dragStart;
          return;
        }
        if (this.dragStart) {
          delete this.dragStart;
        } else {
          this.dragStart = day.date;
        }
        this.selectDay(day.date);
      });
  }

  selectDayMouseUp(dateString: string, event: MouseEvent) {
    if (event.button !== 0) return;
    if (!this.dragStart || this.dragStart === dateString) {
      return;
    }
    delete this.dragStart;
    this.selectDay(dateString);
  }

  selectDay(dateString: string) {
    this.daySelected.emit(format(new Date(dateString), dbDayDateToken));
  }

  setHoveredDayIndex(index: number | undefined) {
    this.hoveredDayIndex$.next(index);
  }
}
